import { SelectOptions, SelectOptionsPreset } from '../types'
import React, { JSX, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import Select from 'live/components/forms/ui/select'

import {
    ChannelsConfigFooter,
    ChannelsConfigRigFloorContainer,
    Label,
    Section,
    StyledSwitch,
    UnitLabel
} from './styles/common'
import { SelectWrapper } from './styles/ChannelStyle'
import { LiveModalWrapper, ModalHeader, ModalTitle } from './styles/ModalStyle'
import { CustomButton } from './styles/ActionButtonStyle'
import i18n from 'live/services/i18n'
import { Input } from 'antd'

export const PERFORMANCE_AND_RIG_FLOOR_KPIS_METHODOLOGY = 'PERFORMANCE_AND_RIG_FLOOR_KPIS_METHODOLOGY'
export const PERFORMANCE_KPIS_METHODOLOGY = 'PERFORMANCE_KPIS_METHODOLOGY'

export const rigFloorMethodologyOptions: SelectOptionsPreset[] = [
    {
        value: PERFORMANCE_AND_RIG_FLOOR_KPIS_METHODOLOGY,
        label: 'Performance & Rig Floor KPIs',
        active: false
    },
    {
        value: PERFORMANCE_KPIS_METHODOLOGY,
        label: 'Performance KPIs',
        active: false
    }
]

export const getActiveOption = (options: SelectOptionsPreset[]): SelectOptionsPreset => {
    return options?.find(it => it?.active)
}

export const timestampToDate = (timestamp: string): string => {
    const date = new Date(Number(timestamp))

    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
}

interface ChannelsConfigurationModalProps {
    title: string
    isOpen: boolean
    setIsOpen: (value: boolean) => void
    setIsUsingInterpolation: (value: boolean) => void
    externalSetInterpolationFrequency: React.Dispatch<React.SetStateAction<number>>
    externalSetMaxDeltaWithoutInterpolation: React.Dispatch<React.SetStateAction<number>>
    currentConfig: {
        useInterpolation: boolean
        interpolationFrequency: string
        maxDeltaWithoutInterpolation: string
    }
}

interface ChannelsConfigInputProps {
    title: string
    value: string
    setValue: (value: string) => void
    disabled: boolean
}

export const ChannelsConfigurationModal = ({
    title,
    isOpen,
    setIsOpen,
    setIsUsingInterpolation,
    externalSetInterpolationFrequency,
    externalSetMaxDeltaWithoutInterpolation,
    currentConfig
}: ChannelsConfigurationModalProps): JSX.Element => {
    const [useInterpolation, setUseInterpolation] = useState<boolean>(currentConfig?.useInterpolation ?? false)
    const [interpolationFrequency, setInterpolationFrequency] = useState<string>(currentConfig?.interpolationFrequency)
    const [maxDeltaWithoutInterpolation, setMaxDeltaWithoutInterpolation] = useState<string>(
        currentConfig?.maxDeltaWithoutInterpolation
    )

    return (
        <ChannelsConfigRigFloorContainer>
            <LiveModalWrapper show={isOpen}>
                <ModalHeader>
                    <ModalTitle>{title}</ModalTitle>
                </ModalHeader>
                <div style={{ padding: '20px' }}>
                    <div style={{ fontSize: '13px', marginBottom: '10px' }}>
                        <StyledSwitch
                            onChange={(checked: boolean) => {
                                setUseInterpolation(checked)
                            }}
                            checked={useInterpolation}
                            size="small"
                        />
                        <span style={{ marginLeft: '5px', fontWeight: 'bold' }}>{i18n('CHANNELS INTERPOLATION')}</span>
                    </div>
                    <ChannelsConfigInput
                        title={i18n('INTERPOLATION FREQUENCY')}
                        value={interpolationFrequency}
                        setValue={setInterpolationFrequency}
                        disabled={false}
                    />
                    <ChannelsConfigInput
                        title={i18n('MAX DELTA WITHOUT INTERPOLATION')}
                        value={maxDeltaWithoutInterpolation}
                        setValue={setMaxDeltaWithoutInterpolation}
                        disabled={false}
                    />
                </div>
                <ChannelsConfigFooter>
                    <CustomButton
                        backgroundColor={'#37a3ef'}
                        borderConfiguration="none"
                        textColor="#FFF"
                        minSize="101px"
                        onClick={() => {
                            setIsUsingInterpolation(useInterpolation)
                            externalSetInterpolationFrequency(Number(interpolationFrequency))
                            externalSetMaxDeltaWithoutInterpolation(Number(maxDeltaWithoutInterpolation))
                            setIsOpen(false)
                        }}
                        style={{ fontWeight: 'bold' }}
                    >
                        {i18n('Apply')}
                    </CustomButton>
                    <Action
                        id="stop-reprocess-btn-kpiOptionsReprocessCancelButton"
                        style={{ marginRight: 5, color: 'var(--gray-800)' }}
                        onClick={() => setIsOpen(false)}
                        className="btn btn-ghost"
                    >
                        {i18n('Close')}
                    </Action>
                </ChannelsConfigFooter>
            </LiveModalWrapper>
        </ChannelsConfigRigFloorContainer>
    )
}

export const ChannelsConfigInput = ({ title, value, setValue, disabled }: ChannelsConfigInputProps): JSX.Element => {
    const cleanInput = (value: string): string => {
        const numericOnly = value.replace(/\D/g, '')
        const sanitized = numericOnly.replace(/^0+/, '') || '0'
        const limited = Math.min(parseInt(sanitized, 10), 99999999)

        return limited.toString()
    }

    const theme = useTheme().themeName as 'dark' | 'light'
    const isDarkMode = theme === 'dark'

    return (
        <div style={{ width: 210, marginBottom: '10px' }}>
            <Label>{title}</Label>
            <UnitLabel>({i18n('SEC')})</UnitLabel>
            <Input
                onChange={e => {
                    setValue(cleanInput(e.target.value))
                }}
                value={value}
                disabled={disabled}
                style={{ backgroundColor: isDarkMode ? 'black' : 'white' }}
            />
        </div>
    )
}

interface EquipmentSelectSectionProps {
    title: string
    titleClassName: string
    tooltipId: string
    tooltipText: string
    dataTestId: string
    linkColor: string
    disabled: boolean
    clearable?: boolean
    options: SelectOptions[]
    value: SelectOptions | undefined
    onChange: (option: SelectOptions | undefined) => void
    linkClassName?: string
    // when true, renders the selected value (or "No equipment selected") as plain read-only text
    // instead of the Select control — used by the Real Time collapsed summary (RS-08/CA-10).
    readOnly?: boolean
}

export const EquipmentSelectSection = ({
    title,
    titleClassName,
    tooltipId,
    tooltipText,
    dataTestId,
    linkColor,
    disabled,
    clearable,
    options,
    value,
    onChange,
    linkClassName,
    readOnly = false
}: EquipmentSelectSectionProps): JSX.Element => {
    return (
        <Section>
            <div className="section-name">
                <span className={titleClassName}>{title}</span>
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip style={{ zIndex: 9999 }} id={tooltipId}>
                            {i18n(tooltipText)}
                        </Tooltip>
                    }
                >
                    <i style={{ color: linkColor, cursor: 'default', fontSize: '10px' }} className="far fa-info-circle" />
                </OverlayTrigger>
            </div>
            <SelectWrapper data-testid={dataTestId}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {readOnly ? (
                        <div style={{ fontSize: '13px', color: 'var(--gray-800)', height: '36px', display: 'flex', alignItems: 'center' }}>
                            {value?.label ?? i18n('No equipment selected')}
                        </div>
                    ) : (
                        <div className="live-select">
                            <Select
                                disabled={disabled}
                                clearable={clearable}
                                searchable={false}
                                options={options}
                                value={value?.value}
                                onChange={(el, ind) => {
                                    onChange(ind)
                                }}
                                placeholder={value?.label}
                            />
                        </div>
                    )}
                    {!readOnly &&
                        (value ? (
                            <a
                                href={`/#/config/equipment-manager/instance/${value?.value}`}
                                className={linkClassName}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {value?.label} link →
                            </a>
                        ) : (
                            <p className={'no-equipment-selected'}>{i18n('No equipment selected')}</p>
                        ))}
                </div>
            </SelectWrapper>
        </Section>
    )
}

interface ChannelsInterpolationBadgeProps {
    active: boolean
    // When true, this is the sole content of its column (the "Rig Floor/Riser channels config"
    // title has moved up into the column's .section-name row) and needs to match the 36px-tall,
    // vertically centered box every other collapsed value uses — not the expanded spacing below.
    collapsed?: boolean
}

const baseLabelStyle = {
    fontFamily: "'SourceSansPro-Bold', Arial, 'Helvetica Neue', Helvetica, sans-serif",
    fontWeight: 700,
    fontSize: '13px',
    lineHeight: '16px',
    color: 'var(--gray-800)'
}

// Expanded: aligns this badge's baseline with the equipment link/"No equipment selected" subtitle
// in the neighboring columns — those sit 6px below their Select (3px from `.live-select`'s own
// margin-bottom plus the paragraph's rendering), while this badge sits directly under a plain
// button with no equivalent spacing.
const expandedLabelStyle = { ...baseLabelStyle, marginTop: '6px' }

// Collapsed: this is the only content in its column, immediately below a now-visible title in
// .section-name — matches the 36px/flex/center box every other collapsed value cell uses.
const collapsedLabelStyle = { ...baseLabelStyle, height: '36px', display: 'flex', alignItems: 'center' }

// RS-06/RS-07: "Channels interpolation: [valor]" badge shown below both the Rig Floor and Riser
// channel-config buttons. Uses inline styles rather than the ambient `.section-name
// label.interpolation-status` CSS — this badge doesn't always render inside a `.section-name`
// wrapper, so that selector wouldn't match.
export const ChannelsInterpolationBadge = ({ active, collapsed = false }: ChannelsInterpolationBadgeProps): JSX.Element => (
    <label className="interpolation-status" style={collapsed ? collapsedLabelStyle : expandedLabelStyle}>
        {i18n('Channels interpolation')}:{' '}
        {active ? (
            <span style={{ color: 'var(--green-700)' }}>{i18n('interpolation-on')}</span>
        ) : (
            <span style={{ color: 'var(--red-700)' }}>{i18n('interpolation-off')}</span>
        )}
    </label>
)

export const Action = styled.button`
    margin-right: 8px;

    &.btn {
        padding: 5px 12px;
        border-radius: 3px;
        font-weight: 600;
    }

    &.btn.btn-primary {
        width: 101px;

        border-color: var(--red-500);
        color: #fff;
        background: var(--red-500);
    }

    &.btn.btn-ghost {
        width: 90px;

        border-color: var(--gray-300);

        color: var(--gray-800);
        background: transparent;
    }

    &:focus {
        outline: none;
    }
`
